<template>
	<div class="grid-flex-container pa-4">
		<v-container
			fluid
			class="flexContainerItem mb-2"
			style="overflow: hidden"
		>
			<div class="d-flex">
				<v-text-field
					v-if="canViewReport"
					v-model="searchReports"
					class="py-0 mr-2"
					append-icon="mdi-magnify"
					label="Search"
					single-line
					hide-details
				/>
				<v-btn
					v-if="canAddReport"
					@click.stop="addReport"
					color="primary"
				>
					<v-icon> mdi-plus</v-icon>
					Add Report
				</v-btn>
			</div>
			<div class="generic-grid-container mt-2">
				<v-data-table
					v-if="canViewReport"
					dense
					hide-default-footer
					disable-pagination
					fixed-header
					loading-text="Loading..."
					:items="reports"
					:headers="reportsHeaders"
					:search.sync="searchReports"
					:loading="loadingReports"
				>
					<template v-slot:[`item.Type`]="{ item }">
						<span v-if="reportHasCustomData(item)">
							Custom Data
						</span>
						<span v-else> Default Data </span>
					</template>

					<template v-slot:[`item.DataSource`]="{ item }">
						{{ reportDataSource(item) }}
					</template>
					<template v-slot:[`item.actions`]="{ item }">
						<export-modal
							:timezone-options="true"
							:execution-options="false"
							:data-scope-options="false"
							@exportModalEvent="onExportModalEvent(item, $event)"
						>
							<template #activator="{ on, attrs }">
								<v-btn
									fab
									small
									icon
									elevation="1"
									class="action-btn"
									color="secondary"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon small>mdi-download</v-icon>
								</v-btn>
							</template>
						</export-modal>

						<v-btn
							v-if="canViewReport"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="previewReport(item)"
						>
							<v-icon color="info" small>mdi-eye</v-icon>
						</v-btn>
						<v-btn
							v-if="canEditReport"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="editReport(item)"
						>
							<v-icon color="primary" small>mdi-pencil</v-icon>
						</v-btn>
						<v-btn
							v-if="canDeleteReport"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="deleteReport(item)"
						>
							<v-icon color="error" small>mdi-delete</v-icon>
						</v-btn>
						<v-btn
							v-if="canCloneReport"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="cloneReport(item)"
						>
							<v-icon small>mdi-content-duplicate</v-icon>
						</v-btn>
					</template>
				</v-data-table>
			</div>
		</v-container>

		<v-container
			fluid
			class="flexContainerItem mb-2"
			style="overflow: hidden"
		>
			<div class="d-flex" v-if="canViewWorkbook">
				<v-text-field
					v-model="searchWorkbooks"
					class="py-0 mr-2"
					append-icon="mdi-magnify"
					label="Search"
					single-line
					hide-details
				/>
				<v-btn
					v-if="canAddWorkbook"
					@click.stop="addWorkbook"
					color="primary"
					class="float-right"
				>
					<v-icon> mdi-plus</v-icon>
					Add Workbook
				</v-btn>
			</div>
			<div class="generic-grid-container mt-2" v-if="canViewWorkbook">
				<v-data-table
					dense
					hide-default-footer
					disable-pagination
					fixed-header
					loading-text="Loading..."
					:items="workbooks"
					:headers="workbooksHeaders"
					:search.sync="searchWorkbooks"
					:loading="loadingWorkbooks"
				>
					<template v-slot:[`item.actions`]="{ item }">
						<export-modal
							:timezone-options="true"
							:execution-options="false"
							:data-scope-options="false"
							@exportModalEvent="
								onExportModalEventWorkbook(item, $event)
							"
						>
							<template #activator="{ on, attrs }">
								<v-btn
									fab
									small
									icon
									elevation="1"
									class="action-btn"
									color="secondary"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon small>mdi-download</v-icon>
								</v-btn>
							</template>
						</export-modal>

						<v-btn
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="previewWorkbook(item)"
						>
							<v-icon color="info" small>mdi-eye</v-icon>
						</v-btn>
						<v-btn
							v-if="canEditWorkbook"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="editWorkbook(item)"
						>
							<v-icon color="primary" small>mdi-pencil</v-icon>
						</v-btn>
						<v-btn
							v-if="canDeleteWorkbook"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="deleteWorkbook(item)"
						>
							<v-icon color="error" small>mdi-delete</v-icon>
						</v-btn>
						<v-btn
							v-if="canCloneWorkBook"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click.stop="cloneWorkBook(item)"
						>
							<v-icon small>mdi-content-duplicate</v-icon>
						</v-btn>
					</template>
				</v-data-table>
			</div>
		</v-container>

		<report-modal
			v-if="showAddReport"
			:showDialog="showAddReport"
			@close="onAddReportModalClose"
		/>
		<report-preview-modal
			v-if="showReportPreviewModal"
			:showDialog="showReportPreviewModal"
			:reportId="reportId"
			@close="onReportPreviewClose"
		/>
		<workbook-preview
			v-if="showWorkbookPreviewModal"
			:showDialog="showWorkbookPreviewModal"
			:workbook="selectedWorkbook"
			@close="onCancelPreviewWorkbook"
		>
		</workbook-preview>
	</div>
</template>
<script>
import { mapGetters } from 'vuex';
import { ReportBuilderGetters } from '../../../store/getters';
import { ReportBuilderActions } from '../../../store/actions';
import ReportModal from './ReportModal.vue';
import WorkbookPreview from './workbook/WorkbookPreview.vue';
import ReportPreviewModal from './reportDetails/reportPreview/ReportPreviewModal.vue';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import CloneDialog from '@/components/core/dialogs/cloneDialog';
import { reportTypes } from '../../../enums/reportTypes';
import permissionsMixin from '../../../mixins/permissionsMixin';
import { Permissions } from '../../../enums/permissionsEnum';
import ExportModal from '../../shared/export/ExportModal.vue';

export default {
	components: {
		ReportModal,
		WorkbookPreview,
		ReportPreviewModal,
		ExportModal,
	},
	name: 'ReportBuilder',
	mixins: [permissionsMixin],
	computed: {
		...mapGetters({
			loadingReports: [ReportBuilderGetters.LOADING_REPORTS],
			loadingWorkbooks: [ReportBuilderGetters.LOADING_WORKBOOKS],
			reports: [ReportBuilderGetters.REPORTS],
			workbooks: [ReportBuilderGetters.WORKBOOKS],
		}),
		canViewReport() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Report.View,
			]);
		},
		canAddReport() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Report.Add,
			]);
		},
		canEditReport() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Report.Edit,
			]);
		},
		canDeleteReport() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Report.Delete,
			]);
		},
		canCloneReport() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Report.Clone,
			]);
		},
		canViewWorkbook() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Workbook.View,
			]);
		},
		canAddWorkbook() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Workbook.Add,
			]);
		},
		canEditWorkbook() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Workbook.Edit,
			]);
		},
		canDeleteWorkbook() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Workbook.Delete,
			]);
		},
		canCloneWorkBook() {
			return this.hasPermissions([
				Permissions.Tools.ReportBuilder.Workbook.Clone,
			]);
		},
	},
	data() {
		return {
			searchReports: null,
			searchWorkbooks: null,
			showWorkbookPreviewModal: false,
			selectedWorkbook: null,
			report: {},
			workbook: {},
			reportsHeaders: [
				{ text: 'Report Name', value: 'Name' },
				{ text: 'Report Type', value: 'Type' },
				{ text: 'Data Source', value: 'DataSource' },
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
			workbooksHeaders: [
				{ text: 'Workbook Name', value: 'Name' },
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
			showAddReport: false,
			showReportPreviewModal: false,
		};
	},
	destroyed() {
		this.$store.dispatch(ReportBuilderActions.CLEAR);
	},
	mounted() {
		this.$store.dispatch(ReportBuilderActions.LOAD_REPORTS);
		this.$store.dispatch(ReportBuilderActions.LOAD_WORKBOOKS);
	},
	methods: {
		onExportModalEvent(item, exportConfig) {
			this.report.ReportId = item.Id;
			this.report.ReportName = item.Name;
			this.report.TimeZone = exportConfig.isLocalTime
				? new Date().getTimezoneOffset() * -1
				: 0;
			const isDirectMode = !exportConfig.isBackgroundExport;
			this.downloadReport(isDirectMode);
		},

		onExportModalEventWorkbook(item, exportConfig) {
			this.workbook.Id = item.Id;
			this.workbook.Name = item.Name;
			this.workbook.TimeZone = exportConfig.isLocalTime
				? new Date().getTimezoneOffset() * -1
				: 0;
			const isDirectMode = !exportConfig.isBackgroundExport;
			this.downloadWorkbook(isDirectMode);
		},

		reportHasCustomData(report) {
			if (!report) return null;
			if (!report.Type) return null;
			return report.Type == reportTypes.CUSTOM_DATA;
		},
		reportDataSource(report) {
			if (!report) return null;
			if (!report.Type) return null;
			if (report.Type == reportTypes.CUSTOM_DATA)
				return report.CustomData;
			if (!report.GridView) return null;
			return report.GridView.Name;
		},

		onReportPreviewClose() {
			this.showReportPreviewModal = false;
		},
		onAddReportModalClose() {
			this.showAddReport = false;
		},
		addReport() {
			this.showAddReport = true;
		},
		editReport(item) {
			this.$router.push({
				name: 'ReportDetails',
				params: { reportId: item.Id },
			});
		},
		deleteReport(item) {
			ConfirmDialog({
				message: 'Are you sure you want to delete this report?',
			}).then((success) => {
				if (!success) return;
				this.$store.dispatch(ReportBuilderActions.DELETE_REPORT, item);
			});
		},

		addWorkbook() {
			this.$router.push({ name: 'WorkbookDetails' });
		},
		editWorkbook(item) {
			this.$router.push({
				name: 'WorkbookDetails',
				params: { workbookId: item.Id },
			});
		},
		deleteWorkbook(item) {
			ConfirmDialog({
				message: 'Are you sure you want to delete this workbook?',
			}).then((success) => {
				if (!success) return;
				this.$store.dispatch(
					ReportBuilderActions.DELETE_WORKBOOK,
					item
				);
			});
		},

		previewReport(report) {
			this.reportId = report.Id;
			this.showReportPreviewModal = true;
		},
		previewWorkbook(workbook) {
			this.selectedWorkbook = workbook;
			this.showWorkbookPreviewModal = true;
		},
		onCancelPreviewWorkbook() {
			this.showWorkbookPreviewModal = false;
			this.selectedWorkbook = null;
		},
		downloadReport(isDirectMode = true) {
			this.$store
				.dispatch(ReportBuilderActions.DOWNLOAD_REPORT, {
					item: this.report,
					isDirectMode: isDirectMode,
				})
				.then(() => {
					this.report = {};
				});
		},

		downloadWorkbook(isDirectMode = true) {
			this.$store
				.dispatch(ReportBuilderActions.DOWNLOAD_WORKBOOK, {
					item: this.workbook,
					isDirectMode: isDirectMode,
				})
				.then(() => {
					this.workbook = {};
				});
		},
		cloneReport(item) {
			CloneDialog({
				message: item.Name,
			}).then((resp) => {
				if (!resp.success) return;
				let payload = {
					SourceId: item.Id,
					DestinationName: resp.name,
				};
				this.$store.dispatch(
					ReportBuilderActions.CLONE_REPORT,
					payload
				);
			});
		},
		cloneWorkBook(item) {
			CloneDialog({
				message: item.Name,
			}).then((resp) => {
				if (!resp.success) return;
				let payload = {
					SourceId: item.Id,
					DestinationName: resp.name,
				};
				this.$store.dispatch(
					ReportBuilderActions.CLONE_WORKBOOK,
					payload
				);
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.flexContainerItem {
	flex-grow: 0.5;
	overflow: hidden;
	display: flex;
	flex-direction: column;
}
</style>
